import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "antd";
import { formatMobileNumberStartWithTwoDigitAreaCode } from "src/utils/format";
import { TextBoxErrorText } from "../../Common/Form/TextBoxForm";
import {
  ButtonTitle,
  Content,
  EditButton,
  InputRow,
  Modal,
  SubtitleBody,
  ModalTitle,
  ReferenceBody,
  ResendButton,
  ResendWait,
  SendOtpButton,
  SubmitOtpButton,
  SubtitleBold,
  OtpField,
  CloseIcon,
  OtpFieldContainer,
} from "./OtpLoginV2.style";
import { OtpLoginModalProp } from "./OtpLogin.types";

export const OtpLoginModal = ({
  disable,
  loading = false,
  otpRef,
  otpVerified,
  delay,
  onClickSend,
  onClickSubmit,
  target,
  form,
  isLoading,
  otpField,
  buttonLabels,
  modalTitle,
  desktopBreakpoint = false,
}: OtpLoginModalProp) => {
  const { t } = useTranslation(["otp-form", "layout"]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const isCounting = count !== 0;
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const inputOtpRef = useRef<any>(null);

  useEffect(() => {
    if (isCounting) {
      if (count === 0) {
        setCount(0);
      }

      const timer = setInterval(() => {
        setCount(count - 1);
        clearInterval(timer);
      }, 1000);
    }
  }, [count, isCounting]);

  const sendOtp = async () => {
    form.resetFields([otpField]);
    await onClickSend(false);
    setIsVisible(true);
  };

  const resendOtp = () => {
    onClickSend(true);
    setCount(delay);
  };

  const onCancel = () => {
    setIsVisible(false);
  };

  const onEdit = () => {
    setIsVisible(false);
  };

  const verifyOtp = () => {
    onClickSubmit();
  };

  const validateOtp = (_, value, submitError) => {
    if (value && value.length < 6) {
      setDisableButton(true);
      return submitError(
        <TextBoxErrorText>{t("otpErrorInvalid")}</TextBoxErrorText>
      );
    }
    setDisableButton(false);
    return submitError();
  };

  const timerForm = () => {
    const minutes =
      Math.floor(count / 60) < 10
        ? `0${Math.floor(count / 60)}`
        : `${Math.floor(count / 60)}`;
    const seconds = count % 60 < 10 ? `0${count % 60}` : `${count % 60}`;
    return `${minutes}:${seconds}`;
  };

  useEffect(() => {
    if (otpVerified) {
      setIsVisible(false);
    }
  }, [otpVerified]);

  useEffect(() => {
    if (isVisible) {
      inputOtpRef.current?.focusInput(0);
    }
  }, [isVisible]);

  return (
    <>
      <SendOtpButton disabled={disable} loading={loading} onClick={sendOtp}>
        <ButtonTitle level={desktopBreakpoint ? 7 : 8}>
          {buttonLabels.submitButtonLabel ?? t("login")}
        </ButtonTitle>
      </SendOtpButton>
      <Modal
        visible={isVisible}
        centered={desktopBreakpoint}
        closable
        onCancel={onCancel}
        closeIcon={
          <CloseIcon
            preview={false}
            src="/static/pages/planPage/otp-close-icon.svg"
            alt=""
            aria-hidden
          />
        }
        title={<ModalTitle>{modalTitle ?? t("titleRegister")}</ModalTitle>}
        footer={
          <SubmitOtpButton
            onClick={verifyOtp}
            disabled={disableButton}
            loading={isLoading}
          >
            {buttonLabels.submitButtonLabel ?? t("iceCream.submitButton")}
          </SubmitOtpButton>
        }
        width={452}
      >
        <Content>
          <SubtitleBody>
            <Row>
              <Col>{t("iceCream.subtitle")}</Col>
            </Row>
            <Row gutter={8}>
              <Col>
                <SubtitleBold>
                  {formatMobileNumberStartWithTwoDigitAreaCode(target)}
                </SubtitleBold>
              </Col>
              <Col>
                {!isCounting && (
                  <EditButton type="button" onClick={onEdit}>
                    {t("editButton")}
                  </EditButton>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <ReferenceBody>
                  {t("iceCream.reference")}: {otpRef}
                </ReferenceBody>
              </Col>
            </Row>
          </SubtitleBody>
          <InputRow justify="space-between" align="top">
            <Col span={24}>
              <OtpFieldContainer
                data-testid="login-otp-field-container"
                name={otpField}
                rules={[
                  {
                    required: true,
                    message: (
                      <TextBoxErrorText>
                        {t("otpErrorRequired")}
                      </TextBoxErrorText>
                    ),
                  },
                  {
                    validator: validateOtp,
                  },
                ]}
                validateFirst
              >
                <OtpField numInputs={6} isInputNum ref={inputOtpRef} />
              </OtpFieldContainer>
            </Col>
          </InputRow>
          <SubtitleBody>{t("iceCream.resendBody")}</SubtitleBody>
          {!isCounting ? (
            <ResendButton type="button" onClick={resendOtp}>
              {t("iceCream.resendButton")}
            </ResendButton>
          ) : (
            <ResendWait>
              {t("waitResend")}
              {timerForm()}
            </ResendWait>
          )}
        </Content>
      </Modal>
    </>
  );
};
